import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { MdArrowBack } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import Visibility from '@mui/icons-material/Visibility'
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined'
import ReplayOutlined from '@mui/icons-material/ReplayOutlined'
import AddIcon from '@mui/icons-material/Add'
import { FaTimes } from 'react-icons/fa'
import AppButton from '../../shared/AppButton'
import AppTable from '../../shared/AppTable'
import CustomFilterInput from '../../shared/CustomFilterInput'
import Pagination from '../../components/Pagination'
import TableSkeletonLoader from '../../common/TableLoading'
import BulkReplaceTabs from './components/BulkReplaceTabs'
import ClaimsBulkSidebarLayout from './components/ClaimsBulkSidebarLayout'
import claims from '../../gql/claims'
import { API_BASE_URL } from '../../config/apiConfig'

ModuleRegistry.registerModules([AllCommunityModule])

const DEFAULT_GRID_ROWS = 100

const defaultDateRange = () => {
  const toDateInput = date => date.toISOString().slice(0, 10)
  const toDate = new Date()
  const fromDate = new Date()
  fromDate.setMonth(fromDate.getMonth() - 1)
  return { fromDate: toDateInput(fromDate), toDate: toDateInput(toDate) }
}

const statusOptions = [
  { id: 'error', name: 'Error' },
  { id: 'Abnormal Request', name: 'Abnormal Request' },
  { id: 'Exception Error', name: 'Exception Error' },
  { id: 'Validation Error', name: 'Validation Error' }
]

const jobStatusOptions = [
  { id: 'processing', name: 'Processing' },
  { id: 'completed', name: 'Completed' },
  { id: 'failed', name: 'Failed' },
  { id: 'reverted', name: 'Reverted' }
]

const emptyReferralRow = () => ({
  document_id: '',
  referral_no: ''
})

const createGridRows = () =>
  Array.from({ length: DEFAULT_GRID_ROWS }, (_, index) => ({
    ...emptyReferralRow(),
    row_no: index + 1
  }))

const getMessage = error =>
  error?.graphQLErrors?.[0]?.message ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong.'

function SummaryCard ({ label, value }) {
  return (
    <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
      <p className='text-[10px] font-medium uppercase tracking-wide text-slate-500'>
        {label}
      </p>
      <p className='mt-1 text-base font-semibold text-slate-800'>
        {value ?? 0}
      </p>
    </div>
  )
}

export default function ClaimReferralBulkUpdate () {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const location = useLocation()
  const gridRef = useRef(null)
  const isUpdatePage = location.pathname.includes('/replace')
  const defaultDates = useMemo(defaultDateRange, [])

  const [fromDate, setFromDate] = useState(defaultDates.fromDate)
  const [toDate, setToDate] = useState(defaultDates.toDate)
  const [outcomeStatus, setOutcomeStatus] = useState('Validation Error')
  const [inputMode, setInputMode] = useState('manual')
  const [file, setFile] = useState(null)
  const [rows, setRows] = useState(createGridRows)
  const [result, setResult] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [logsPage, setLogsPage] = useState(1)
  const [logFilters, setLogFilters] = useState({
    job_id: '',
    status: '',
    limit: 20,
    page: 1
  })

  const [bulkUpdate, { loading: updating }] = useMutation(
    claims.BULK_UPDATE_CLAIM_REFERRALS,
    { onError: error => toast.error(getMessage(error)) }
  )
  const [
    loadLogs,
    { data: logsData, loading: loadingLogs, refetch: refetchLogs }
  ] = useLazyQuery(claims.LIST_CLAIM_REFERRAL_BULK_LOGS, {
    fetchPolicy: 'network-only',
    onError: error => toast.error(getMessage(error))
  })

  const logs = logsData?.listClaimReferralBulkLogs?.data || []
  const logPages = logsData?.listClaimReferralBulkLogs?.pages || 1

  const fetchLogs = (page = logsPage, filters = logFilters) => {
    const input = {
      ...filters,
      page,
      limit: filters.limit || 20
    }
    setLogsPage(page)
    loadLogs({ variables: { input } })
  }

  useEffect(() => {
    if (!isUpdatePage) fetchLogs(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatePage])

  const mappingColumns = useMemo(
    () => [
      {
        headerName: '#',
        field: 'row_no',
        width: 48,
        minWidth: 48,
        maxWidth: 56,
        editable: false,
        filter: false,
        cellClass: 'text-center text-xs text-gray-500'
      },
      {
        headerName: 'Patient Document Id',
        field: 'document_id',
        editable: true,
        flex: 1,
        minWidth: 220,
        cellEditorParams: { maxLength: 50 }
      },
      {
        headerName: 'Referral',
        field: 'referral_no',
        editable: true,
        flex: 1,
        minWidth: 220,
        cellEditorParams: { maxLength: 50 }
      },
      {
        headerName: 'Actions',
        width: 85,
        minWidth: 85,
        maxWidth: 85,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: params => (
          <button
            type='button'
            onClick={() =>
              setRows(currentRows =>
                currentRows.length > 1
                  ? currentRows
                    .filter((_, index) => index !== params.node.rowIndex)
                    .map((row, index) => ({ ...row, row_no: index + 1 }))
                  : currentRows
              )
            }
            disabled={rows.length === 1}
            className='inline-flex h-5 w-5 items-center justify-center rounded text-red-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-300'
            title='Remove'
          >
            <FaTimes size={11} />
          </button>
        )
      }
    ],
    [rows.length]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      editable: true,
      minWidth: 96,
      width: 112
    }),
    []
  )

  const updateGridRow = event => {
    const { colDef, newValue, rowIndex } = event
    setRows(currentRows =>
      currentRows.map((row, index) =>
        index === rowIndex ? { ...row, [colDef.field]: newValue || '' } : row
      )
    )
  }

  const handleCellFocused = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (!colDef?.editable || params.rowIndex == null) return

    setTimeout(() => {
      if (params.api.getEditingCells().length > 0) return
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleCellClicked = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (params.rowIndex == null || !colDef?.editable) return
    if (params.api.getEditingCells().length > 0) return

    setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleGridReady = params => {
    params.api.setFocusedCell(0, 'document_id')
    params.api.startEditingCell({
      rowIndex: 0,
      colKey: 'document_id'
    })
  }

  const validateBase = () => {
    if (!fromDate || !toDate) {
      toast.error('From date and to date are required.')
      return false
    }
    if (!outcomeStatus) {
      toast.error('Outcome status is required.')
      return false
    }
    if (fromDate > toDate) {
      toast.error('From Date cannot be after To Date.')
      return false
    }
    return true
  }

  const submitManual = async () => {
    if (!validateBase()) return
    const gridApi = gridRef.current?.api
    if (gridApi) gridApi.stopEditing()

    const currentRows = []
    gridApi?.forEachNode(node => currentRows.push(node.data))

    const populatedRows = currentRows.filter(row =>
      ['document_id', 'referral_no'].some(field =>
        String(row[field] || '').trim()
      )
    )

    const validManualRows = populatedRows.map(row => ({
      document_id: String(row.document_id || '').trim(),
      referral_no: String(row.referral_no || '').trim()
    }))

    if (validManualRows.length === 0) {
      toast.error('Add at least one referral row.')
      return
    }
    const hasIncompleteRow = validManualRows.some(
      row => !row.document_id || !row.referral_no
    )
    if (hasIncompleteRow) {
      toast.error('Each filled row must have Patient Document Id and Referral.')
      return
    }
    const duplicateDoc = validManualRows.find(
      (row, index) =>
        validManualRows.findIndex(item => item.document_id === row.document_id) !==
        index
    )
    if (duplicateDoc) {
      toast.error(`Duplicate document ID: ${duplicateDoc.document_id}`)
      return
    }

    const response = await bulkUpdate({
      variables: {
          input: {
            from_date: fromDate,
            to_date: toDate,
            outcome_status: outcomeStatus,
            referrals: validManualRows
          }
      }
    })
    const payload = response.data?.bulkUpdateClaimReferrals
    if (![200, 202, '200', '202'].includes(payload?.status)) {
      toast.error(payload?.message || 'Referral bulk update failed.')
      return
    }
    setResult(payload.data)
    toast.success(payload?.message || 'Referral bulk update started.')
    refetchLogs?.()
  }

  const submitExcel = async () => {
    if (!validateBase()) return
    if (!file) {
      toast.error('Please select an Excel file.')
      return
    }
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error('Only .xlsx and .xls files are supported.')
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('from_date', fromDate)
      formData.append('to_date', toDate)
      formData.append('outcome_status', outcomeStatus)
      formData.append('file', file)

      const token = localStorage.getItem('token')?.replaceAll('"', '')
      const response = await axios.post(
        `${API_BASE_URL}/claim/referral/bulk-update`,
        formData,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      )
      setResult(response.data?.data)
      toast.success(
        response.data?.message || 'Referral bulk update started successfully.'
      )
      refetchLogs?.()
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setUploading(false)
    }
  }

  const downloadTemplate = async () => {
    try {
      setDownloadingTemplate(true)
      const token = localStorage.getItem('token')?.replaceAll('"', '')
      const response = await axios.get(
        `${API_BASE_URL}/claim/referral/bulk-update/template`,
        {
          responseType: 'blob',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'claim_referral_bulk_update_template.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Template downloaded.')
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const handleFileDrop = event => {
    event.preventDefault()
    setDragOver(false)
    const droppedFile = event.dataTransfer?.files?.[0]
    if (droppedFile && /\.xlsx?$/i.test(droppedFile.name)) {
      setFile(droppedFile)
    } else if (droppedFile) {
      toast.error('Please upload an .xlsx or .xls file.')
    }
  }

  const logColumns = [
    {
      header: 'Actions',
      align: 'center',
      render: job => (
        <button
          type='button'
          title='View details'
          onClick={() =>
            navigate('/claims/referral-bulk-update/job/' + job.job_id)
          }
          className='text-gray-500 hover:text-blue-600'
        >
          <Visibility sx={{ fontSize: 19 }} />
        </button>
      )
    },
    { header: 'Job ID', field: 'job_id' },
    { header: 'Status', field: 'status' },
    { header: 'Outcome', field: 'outcome_status' },
    { header: 'Referrals', field: 'referral_count', align: 'right' },
    { header: 'Claims Updated', field: 'total_claims_updated', align: 'right' },
    {
      header: 'Created At',
      render: row =>
        row.created_at ? new Date(row.created_at).toLocaleString() : '—'
    }
  ]

  const resultStats = result
    ? [
        ['Job ID', result.job_id],
        ['Status', result.status],
        ['Outcome', result.outcome_status],
        ['Referrals', result.referral_count],
        ['Claims Updated', result.total_claims_updated]
      ]
    : []

  return (
    <ClaimsBulkSidebarLayout>
      <div className='w-full'>
        <div className='mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center'>
          <div className='flex items-center gap-2'>
            {isUpdatePage ? (
              <div
                onClick={() => navigate('/claims/referral-bulk-update')}
                className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
                title='Back to referral update logs'
              >
                <MdArrowBack
                  className={
                    isRTL
                      ? 'text-white h-4 w-4 rotate-180'
                      : 'text-white h-4 w-4'
                  }
                />
              </div>
            ) : null}
            <div>
              <h1 className='text-lg font-semibold text-black/70'>
                {isUpdatePage ? 'Claim Referral Bulk Update' : ''}
              </h1>
              <p className='text-[11px] text-slate-500'>
                {isUpdatePage
                  ? 'Bulk update pre-auth referral numbers by patient document ID within a date range.'
                  : ''}
              </p>
            </div>
          </div>
          {!isUpdatePage && (
            <AppButton
              label='Update Referrals'
              startIcon={<AddIcon />}
              onClick={() => navigate('/claims/referral-bulk-update/replace')}
            />
          )}
          {isUpdatePage && (
            <div className='flex items-center gap-3'>
              <div className='flex gap-1 rounded-md border border-slate-200 p-0.5'>
                {['manual', 'excel'].map(mode => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => setInputMode(mode)}
                    className={`rounded px-3 py-1.5 text-xs font-medium ${
                      inputMode === mode
                        ? 'bg-[#0070C2] text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {mode === 'excel'
                      ? 'Update Referrals by Excel'
                      : 'Manual Update'}
                  </button>
                ))}
              </div>
              {inputMode === 'excel' && (
                <button
                  type='button'
                  onClick={downloadTemplate}
                  disabled={downloadingTemplate}
                  className='rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60'
                >
                  {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className='w-full'>
          {isUpdatePage && (
            <div>
              <div className='mb-2 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white px-4 py-2.5'>
              <CustomFilterInput
                label={
                  <span>
                    {t('From Date')} <span className='text-red-500'>*</span>
                  </span>
                }
                type='date'
                name='fromDate'
                id='fromDate'
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                placeholder={t('From Date')}
                className='w-full sm:w-[180px]'
              />
              <CustomFilterInput
                label={
                  <span>
                    {t('To Date')} <span className='text-red-500'>*</span>
                  </span>
                }
                type='date'
                name='toDate'
                id='toDate'
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                placeholder={t('To Date')}
                className='w-full sm:w-[180px]'
              />
              <CustomFilterInput
                label={t('Outcome Status')}
                name='outcomeStatus'
                id='outcomeStatus'
                value={outcomeStatus}
                onChange={e => setOutcomeStatus(e.target.value)}
                placeholder={t('Outcome Status')}
                isSelect
                options={statusOptions}
                className='w-full sm:w-[180px]'
              />
              </div>

            {inputMode === 'manual' ? (
              <div className='mt-2 relative'>
                <div className='ag-theme-quartz referral-bulk-update-grid h-[calc(100vh-270px)] w-full overflow-hidden rounded-md border border-slate-200'>
                  <AgGridReact
                    ref={gridRef}
                    columnDefs={mappingColumns}
                    rowData={rows}
                    defaultColDef={defaultColDef}
                    enableRtl={isRTL}
                    animateRows
                    stopEditingWhenCellsLoseFocus
                    undoRedoCellEditing
                    undoRedoCellEditingLimit={50}
                    suppressRowClickSelection
                    onCellValueChanged={updateGridRow}
                    onCellFocused={handleCellFocused}
                    onCellClicked={handleCellClicked}
                    onGridReady={handleGridReady}
                  />
                </div>
                <div className='mt-1 flex justify-end'>
                  <button
                    type='button'
                    className='flex items-center gap-1 text-xs text-[#0070C2] underline disabled:text-slate-400'
                    onClick={() =>
                      setRows(currentRows => [
                        ...currentRows,
                        { ...emptyReferralRow(), row_no: currentRows.length + 1 }
                      ])
                    }
                    disabled={updating}
                  >
                    <AddIcon fontSize='small' /> Add New Row
                  </button>
                </div>
              </div>
            ) : (
              <div className='mt-2'>
                <div
                  onDrop={handleFileDrop}
                  onDragOver={e => {
                    e.preventDefault()
                    setDragOver(true)
                  }}
                  onDragLeave={() => setDragOver(false)}
                  className={`relative flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                    dragOver
                      ? 'border-[#0070C2] bg-blue-50'
                      : file
                        ? 'border-[#0070C2] bg-blue-50'
                        : 'border-slate-300 bg-slate-50 hover:border-[#0070C2] hover:bg-blue-50/40'
                  }`}
                >
                  {file ? (
                    <>
                      <button
                        type='button'
                        onClick={e => {
                          e.stopPropagation()
                          setFile(null)
                        }}
                        className='absolute right-2 top-2 text-slate-400 hover:text-red-500'
                      >
                        <FaTimes size={16} />
                      </button>
                      <FileUploadOutlined className='mb-2 text-3xl text-[#0070C2]' />
                      <p className='max-w-full truncate px-8 text-xs font-semibold text-slate-700'>
                        {file.name}
                      </p>
                    </>
                  ) : (
                    <>
                      <FileUploadOutlined
                        className={`mb-2 text-3xl ${
                          dragOver ? 'text-[#0070C2]' : 'text-slate-400'
                        }`}
                      />
                      <p className='text-xs font-semibold text-slate-600'>
                        Drag and drop your Excel file here
                      </p>
                      <p className='mt-0.5 text-[10px] text-slate-400'>
                        Required headers: Patient Document Id, Referral
                      </p>
                      <input
                        type='file'
                        hidden
                        id='referral-bulk-file'
                        accept='.xls,.xlsx'
                        onChange={e => {
                          setFile(e.target.files?.[0] || null)
                          setDragOver(false)
                        }}
                      />
                      <label htmlFor='referral-bulk-file' className='mt-3'>
                        <AppButton
                          label='Browse files'
                          variant='outlined'
                          bgColor='transparent'
                          onClick={() =>
                            document.getElementById('referral-bulk-file').click()
                          }
                        />
                      </label>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className='mt-2 flex justify-end gap-2 border-t border-slate-200 pt-2'>
              <AppButton
                label='Reset'
                variant='outlined'
                bgColor='transparent'
                disabled={updating || uploading}
                onClick={() => {
                  setFromDate(defaultDates.fromDate)
                  setToDate(defaultDates.toDate)
                  setOutcomeStatus('approved')
                  setRows(createGridRows())
                  setFile(null)
                  setResult(null)
                }}
              />
              <AppButton
                label={
                  inputMode === 'manual'
                    ? 'Update Referrals'
                    : 'Upload Referral Excel'
                }
                startIcon={
                  inputMode === 'manual' ? (
                    <ReplayOutlined />
                  ) : (
                    <FileUploadOutlined />
                  )
                }
                onClick={inputMode === 'manual' ? submitManual : submitExcel}
                isLoading={updating || uploading}
                loadingText='Processing...'
              />
            </div>

            {result && (
              <div className='mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2'>
                <div className='mb-2 flex items-center justify-between gap-2'>
                  <p className='text-xs font-semibold text-emerald-800'>
                    Referral bulk update started
                  </p>
                  <AppButton
                    label='View job details'
                    variant='outlined'
                    bgColor='transparent'
                    onClick={() =>
                      navigate('/claims/referral-bulk-update/job/' + result.job_id)
                    }
                  />
                </div>
                <div className='grid grid-cols-2 gap-2 md:grid-cols-5'>
                  {resultStats.map(([label, value]) => (
                    <SummaryCard key={label} label={label} value={value} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!isUpdatePage && (
          <>
            {/* <div className='mb-2 grid grid-cols-1 items-end gap-2 md:grid-cols-4'>
              <CustomFilterInput
                label='Job ID'
                value={logFilters.job_id}
                onChange={e =>
                  setLogFilters(prev => ({ ...prev, job_id: e.target.value }))
                }
              />
              <CustomFilterInput
                label='Status'
                value={logFilters.status}
                onChange={e =>
                  setLogFilters(prev => ({ ...prev, status: e.target.value }))
                }
                isSelect
                options={jobStatusOptions}
              />
              <AppButton
                label='Filter'
                onClick={() => fetchLogs(1)}
                isLoading={loadingLogs}
              />
            </div> */}
            <AppTable
              columns={logColumns}
              data={logs}
              loading={loadingLogs}
              actions={false}
              bodyRowPadding='7px'
              LoaderComponent={
                <TableSkeletonLoader columnCount={7} rowCount={8} />
              }
            />
            <div className='mt-2'>
              <Pagination
                currentPage={logsPage}
                totalPages={logPages}
                onPageChange={page => fetchLogs(page)}
              />
            </div>
          </>
        )}
        <style>
          {`
          .referral-bulk-update-grid {
            --ag-border-color: #9ca3af;
            --ag-row-border-color: #cbd5e1;
            --ag-header-background-color: #e5e7eb;
            --ag-header-foreground-color: #111827;
            --ag-odd-row-background-color: #f8fafc;
            --ag-cell-horizontal-border: solid #cbd5e1;
            --ag-row-height: 30px;
            --ag-header-height: 32px;
            --ag-font-size: 12px;
          }

          .referral-bulk-update-grid .ag-root-wrapper,
          .referral-bulk-update-grid .ag-header,
          .referral-bulk-update-grid .ag-cell {
            border-color: #cbd5e1;
          }

          .referral-bulk-update-grid .ag-header-cell,
          .referral-bulk-update-grid .ag-header-group-cell {
            border-right: 1px solid #9ca3af;
            font-weight: 700;
          }

          .referral-bulk-update-grid .ag-cell {
            border-right: 1px solid #cbd5e1;
            border-bottom: 1px solid #e2e8f0;
            padding-left: 6px;
            padding-right: 6px;
          }

          .referral-bulk-update-grid .ag-cell-focus {
            outline: none;
          }

          .referral-bulk-update-grid .ag-cell-inline-editing {
            padding: 0 !important;
            outline: 2px solid #0070c2;
            outline-offset: -2px;
          }

          .referral-bulk-update-grid .ag-cell-edit-wrapper,
          .referral-bulk-update-grid .ag-cell-editor,
          .referral-bulk-update-grid .ag-text-field,
          .referral-bulk-update-grid .ag-wrapper,
          .referral-bulk-update-grid .ag-input-wrapper,
          .referral-bulk-update-grid input.ag-input-field-input {
            width: 100% !important;
            height: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box;
          }

          .referral-bulk-update-grid input.ag-input-field-input {
            padding: 0 6px !important;
          }
        `}
        </style>
      </div>
      </div>
    </ClaimsBulkSidebarLayout>
  )
}
